import {
  isAddressCompatibleWithChain,
  isWithdrawalRisky,
  useFeatureGate,
} from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'

import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'

export const WITHDRAWAL_RISK_QUERY_KEY = 'withdrawalRisk'

export interface WithdrawalRiskResult {
  /** `true` blocks. `undefined` while unknown — which also blocks. */
  isRisky: boolean | undefined
  isChecking: boolean
  isError: boolean
}

/**
 * Chainalysis screening for the recipient.
 *
 * **Fails closed**: callers must require `isRisky === false`, so in-flight and
 * errored both block. The chain id scopes the screen to the destination chain,
 * so it is part of the key (web does the same).
 */
export function useWithdrawalRisk(
  address: string,
  chainId: number | undefined,
): WithdrawalRiskResult {
  const { apiKey } = useFunkitIdentity()
  const screeningDisabled = useFeatureGate('disable-frontend-chainalysis')

  const query = useQuery({
    queryKey: [WITHDRAWAL_RISK_QUERY_KEY, apiKey, address, chainId],
    queryFn: () =>
      isWithdrawalRisky({
        address,
        chainId: chainId?.toString(),
        apiKey,
        logger,
        screeningDisabled,
      }),
    // Only screen an address that could actually exist on the destination
    // chain. Without this the query fires on every keystroke of a
    // half-typed address, one Chainalysis call per character.
    enabled:
      !!apiKey &&
      !!address &&
      chainId !== undefined &&
      isAddressCompatibleWithChain(address, chainId),
    // api-base already retries the request; one more attempt before the error
    // state blocks the CTA is enough.
    retry: 1,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  return {
    isRisky: query.data,
    isChecking: query.isFetching,
    isError: query.isError,
  }
}
